import { useMutation } from '@apollo/client';
import { sendMessageSecureV2 } from '../../graphql/messaging';
import { classifyError, ERROR_KIND } from '../../util/classifyError';

const buildSendMessageError = (error) => {
  const classified = classifyError(error, { operation: 'mutation' });
  const sendError = error instanceof Error ? error : new Error(classified.message);
  sendError.userFacing = true;

  if (classified.kind === ERROR_KIND.NETWORK) {
    sendError.displayMessage = 'Die Nachricht konnte nicht gesendet werden. Bitte prüfe deine Verbindung und versuche es erneut.';
  } else if (classified.kind === ERROR_KIND.AUTH) {
    sendError.displayMessage = 'Deine Sitzung ist abgelaufen. Bitte melde dich erneut an und versuche es erneut.';
  } else if (classified.kind === ERROR_KIND.BLOCKED || classified.kind === ERROR_KIND.VALIDATION) {
    sendError.displayMessage = classified.message;
  } else {
    sendError.displayMessage = 'Die Nachricht konnte nicht gesendet werden. Bitte versuche es erneut.';
  }

  sendError.errorKind = classified.kind;
  return sendError;
};

const throwSendMessageError = (message, kind = ERROR_KIND.BLOCKED) => {
  const sendError = new Error(message);
  sendError.userFacing = true;
  sendError.errorKind = kind;
  sendError.displayMessage = message;
  throw sendError;
};

export const useCreateMessage = () => {
  const [sendMessageMutation, { loading: sendingMessage }] = useMutation(sendMessageSecureV2);

  const sendMessage = async ({ content, chatRoomId, senderID, chatRoom }) => {
    if (!content.trim()) {
      return;
    }

    // Validate required parameters
    if (!chatRoomId) {
      throwSendMessageError('Diese Unterhaltung konnte nicht geöffnet werden.', ERROR_KIND.VALIDATION);
    }

    if (chatRoom) {
      if (chatRoom.chatStatus === 'READ_ONLY' && chatRoom.chatType === 'INQUIRY') {
        throwSendMessageError('Diese Anfrage ist abgeschlossen. Bitte schreibe im Buchungs-Chat weiter.');
      }

      if (chatRoom.chatStatus === 'DELETED') {
        throwSendMessageError('Dieser Chat wurde gelöscht.');
      }

      if (chatRoom.participantIDs && Array.isArray(chatRoom.participantIDs) && senderID) {
        if (!chatRoom.participantIDs.includes(senderID)) {
          throwSendMessageError('Du bist nicht in diesem Chat.');
        }
      }
    }
    
    try {
      const input = {
        chatRoomID: chatRoomId,
        content: content.trim(),
        type: 'TEXT'
      };
      
      const response = await sendMessageMutation({
        variables: {
          input,
        },
        context: { skipGlobalError: true },
      });
      
      const createdMessage = response.data?.sendMessageSecure;
      
      if (!createdMessage) {
        throw new Error('No message data returned from server');
      }
      
      return { data: { createMessage: createdMessage } };
    } catch (error) {
      throw buildSendMessageError(error);
    }
  };

  return {
    sendMessage,
    sendingMessage
  };
};

export default useCreateMessage;
